import { Container, Divider, SxProp } from 'theme-ui';
import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useNavigationQuery from '~/hooks/graphql/queries/use-navigation';
import LinkRenderer from '~/components/Generic/LinkRenderer';
import { isNavigationItem, isContentfulPage } from '~/utils/nav';

type Props = {
  isOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  forwardSx?: SxProp['sx'];
};

const NavMenu = React.memo(function NavMenu({ isOpen, setIsMenuOpen, forwardSx = {} }: Props) {
  const { mobile, secondary } = useNavigationQuery();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.nav
          aria-label="Site navigation menu"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          sx={{
            position: 'fixed',
            top: 'calc(var(--nav-height) + var(--notification-height))',
            right: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'black',
            overflow: 'auto',
            zIndex: 15,
            ...forwardSx,
          }}
        >
          <Container
            variant="navContainer"
            sx={{
              position: 'absolute',
              display: ['flex', null, 'none'],
              flexFlow: 'column nowrap',
              justifyContent: 'flex-start',
              height: '100%',
              color: 'white',
              p: '0 2.4rem',
              pb: 'calc(env(safe-area-inset-bottom) + 2.4rem)',
            }}
          >
            <div
              sx={{
                paddingTop: '4.3rem', // remove 1 rem from padding
                overflowY: 'visible',
                flexShrink: 0,
                height: 'fit-content',
              }}
            >
              <ul
                sx={{
                  variant: 'list.bare',
                  fontWeight: 'medium',
                  textTransform: 'uppercase',
                  m: 0,
                }}
              >
                {mobile?.map((item) => {
                  if (!item) return null;
                  if (!isNavigationItem(item)) return null;
                  return (
                    <li sx={{ ':not(:last-of-type)': { marginBottom: '2rem' } }} key={item.id}>
                      <LinkRenderer
                        onClick={() => setIsMenuOpen(false)}
                        locationDetail="nav"
                        page={item}
                        title={isContentfulPage(item) ? item.title : item.text}
                        type={item.__typename}
                        slug={isContentfulPage(item) ? item.slug : item.innerLink}
                        sx={{
                          fontSize: '2.4rem',
                          fontWeight: 400,
                          lineHeight: '27px',
                          letterSpacing: '-1%',
                        }}
                      >
                        {isContentfulPage(item) ? item.title : item.text}
                      </LinkRenderer>
                    </li>
                  );
                })}
              </ul>

              <Divider color="UIGray3" sx={{ my: '5.6rem' }}></Divider>

              <nav aria-label="Company">
                <ul
                  sx={{
                    variant: 'list.bare',
                    fontWeight: 'text',
                    textTransform: 'capitalize',
                    m: 0,
                    marginBottom: '2.4rem',
                    fontSize: '1.8rem',
                    lineHeight: '1.25rem',
                    color: 'UIGray1',
                  }}
                >
                  {secondary?.map((page) => {
                    if (!page) return null;
                    if (!isNavigationItem(page)) return null;
                    return (
                      <li sx={{ ':not(:last-of-type)': { marginBottom: '2.4rem' } }} key={page.id}>
                        <LinkRenderer
                          onClick={() => setIsMenuOpen(false)}
                          locationDetail="nav"
                          page={page}
                          title={isContentfulPage(page) ? page.title : page.text}
                          type={page.__typename}
                          slug={isContentfulPage(page) ? page.slug : page.innerLink}
                        >
                          {isContentfulPage(page) ? page.title : page.text}
                        </LinkRenderer>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
            {/* <SocialLinks theme="dark" /> */}
          </Container>
        </motion.nav>
      )}
    </AnimatePresence>
  );
});

export default NavMenu;
